import React, { useMemo, useState, useCallback } from 'react';
import { getContactFullName, getEmail } from '../../../components/entitlement-page-utils';
import { useQuery } from '@apollo/client';
import { EntitlementDetails } from '@css-xp/graphtypes/src/generated-types-new';
import { CONTACT_PATH_2 } from '@atlassian/cssxp-typescommon';
import { Link } from 'react-router-dom';
import Avatar from '@atlaskit/avatar';
import { FormattedDate, DynamicResultTable, CopyableText, NoDataContentInTable, useFeatureToggle } from '@css-xp/base';
import { DEFAULT_ROWS_PER_PAGE, DEFAULT_PAGE, NO_DETAILS_TO_SHOW } from '@atlassian/cssxp-utils-entitlement';
import './contacts-tab.scss';
import { ENABLE_AAID_FOR_CONTACT_PAGE_REDIRECTION } from '@atlassian/cssxp-utils-generic';
import memoize from 'lodash/memoize';
import { fetchTransactionAccountForEntitlement } from '../../../queries/fetch-transaction-account-for-entitlement';

const getContactName = memoize(
  (user: any) => {
    return user?.fullName ?? getEmail(user?.email);
  },
  (...args) => {
    return JSON.stringify(args);
  }
);

const columnHeaders = [
  {
    content: 'Name',
    width: 30,
  },
  {
    content: 'Email',
    width: 60,
  },
  {
    content: 'Last logged in',
    width: 30,
  },
];

const BillingAdminsContactTable = ({ entitlement }: { entitlement: EntitlementDetails }) => {
  const getEntitlementID = () => {
    if (entitlement?.parentSen) {
      return entitlement?.parentSen;
    }
    return entitlement.id;
  };

  const headData = useMemo(() => {
    const cells = columnHeaders;
    return {
      cells,
    };
  }, []);

  const emptyStateOrderProp = {
    header: '',
    description: NO_DETAILS_TO_SHOW,
  };

  const [rowsPerPage, setRowsPerPage] = useState<number>(DEFAULT_ROWS_PER_PAGE);
  const [currentPage, setCurrentPage] = useState<number>(DEFAULT_PAGE);
  const [isEnabled] = useFeatureToggle();

  const { data, loading, refetch } = useQuery(fetchTransactionAccountForEntitlement, {
    variables: {
      entitlementId: getEntitlementID(),
      searchOptions: { first: rowsPerPage, offset: 0 },
    },
    displayName: `fetchEntitlementBillingAdmins`,
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-and-network',
    skip: !getEntitlementID(),
  });

  const onPageChange = useCallback(
    (newPage: number) => {
      setCurrentPage(newPage);
      refetch({
        searchOptions: {
          first: rowsPerPage,
          offset: newPage !== 1 ? newPage * rowsPerPage - rowsPerPage : 0,
        },
      });
    },
    [rowsPerPage, refetch]
  );

  const handleRowsChange = useCallback((rowsCount: number) => {
    setRowsPerPage(rowsCount);
    setCurrentPage(1);
  }, []);

  const transactionAccount = data?.entitlementDetails?.transactionAccount;
  const billAdminNodes = transactionAccount?.billingAdmins?.nodes ?? [];
  const billAdminsTotalCount = transactionAccount?.billingAdmins?.totalCount;

  const createRows = (): Array<any> => {
    const result = billAdminNodes.map((billingAdmin: any) => {
      const { email: primaryEmail, fullName: displayName, atlassianAccountId, lastActive } = billingAdmin;
      const email = getEmail(primaryEmail);

      return {
        cells: [
          {
            content: (
              <>
                <Avatar src={undefined} size="small" />
                <Link
                  to={`${CONTACT_PATH_2}/${
                    isEnabled(ENABLE_AAID_FOR_CONTACT_PAGE_REDIRECTION) && atlassianAccountId
                      ? atlassianAccountId
                      : window.btoa(email !== 'N/A' ? email : '')
                  }`}
                  data-testid="contact-link"
                  onClick={(e) => e.stopPropagation()}
                  style={{ verticalAlign: 'super' }}
                  title={getContactFullName(displayName)}
                >
                  {getContactName(billingAdmin)}
                </Link>
              </>
            ),
          },
          {
            content: (
              <span>
                {' '}
                <CopyableText text={email} />
              </span>
            ),
          },
          {
            content: (
              <span>
                {' '}
                {lastActive ? <FormattedDate date={lastActive} showTime={true} /> : <NoDataContentInTable />}
              </span>
            ),
          },
        ],
      };
    });
    return result;
  };

  return (
    <DynamicResultTable
      className="contacts-table"
      testId="contacts-billing-admin-tab"
      headingText="Billing Admins"
      head={headData}
      rows={createRows()}
      emptyStateProps={emptyStateOrderProp}
      isLoading={loading}
      totalCount={billAdminsTotalCount}
      currentPage={currentPage}
      rowsPerPage={rowsPerPage}
      onPageChange={onPageChange}
      isPaginationEnabled={billAdminsTotalCount > rowsPerPage}
      onRowChange={handleRowsChange}
    />
  );
};

export default BillingAdminsContactTable;
